import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

export default function ContactPage2() {
  const [result, setResult] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setResult("Sending...");
    const formData = new FormData(event.target);
    formData.append("access_key", "79ee9b3c-33b2-465e-9ef3-b9e73565cbf3");

    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (data.success) {
      setResult("Form submitted successfully!");
      event.target.reset();
    } else {
      console.error("Error:", data);
      setResult(data.message || "Something went wrong.");
    }
  };

  return (
    <main className="bg-white text-gray-800">
      <Helmet>
        <meta
          name="description"
          content="Get in touch with Codexa. We'd love to hear from you regarding your ideas, questions, or project collaboration."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-indigo-600 py-20 text-white text-center">
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Let’s Connect</h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Have a question, feedback, or opportunity? We’re always here to help
            you build something great.
          </p>
        </motion.div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gray-50 p-8 rounded-2xl shadow-xl space-y-8"
        >
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold text-indigo-600">Contact Us</h2>
            <p className="text-gray-600">We usually respond within 24 hours.</p>
          </div>

          <form className="grid md:grid-cols-2 gap-6" onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your full name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                name="message"
                placeholder="Write your message here..."
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
              ></textarea>
            </div>

            <div className="md:col-span-2 text-center space-y-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="bg-indigo-600 text-white px-8 py-3 rounded-lg text-lg font-medium shadow-lg hover:bg-indigo-700 transition"
              >
                Send Message
              </motion.button>
              <p className="text-sm text-gray-600">{result}</p>

              {/* Email Button with Icon */}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                target="_blank"
                href={`mailto:thecodexaoffical@gmail.com?subject=Project%20Inquiry%20-%20Web%20%2F%20App%20Development&body=Hello%20Codexa%20Team%2C%0A%0AI%20am%20interested%20in%20developing%20a%20new%20website%20%2F%20mobile%20application%20for%20my%20business.%0A%0ABelow%20are%20some%20details%20about%20my%20requirements%3A%0A-%20Type%20of%20project%3A%20%5BWeb%20App%2C%20Mobile%20App%2C%20Website%5D%0A-%20Industry%3A%20%5BYour%20Industry%5D%0A-%20Key%20Features%20Needed%3A%20%5BLogin%2C%20Dashboard%2C%20Payments%2C%20Chat%2C%20etc.%5D%0A-%20Estimated%20Timeline%3A%20%5BASAP%2C%201-3%20Months%2C%20etc.%5D%0A-%20Budget%20Range%3A%20%5BOptional%5D%0A%0APlease%20get%20back%20to%20me%20to%20discuss%20how%20we%20can%20collaborate.%0A%0AThank%20you%2C%0A%5BYour%20Name%5D%0A%5BYour%20Company%5D%0A%5BYour%20Phone%20or%20Email%5D`}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold shadow hover:bg-indigo-700 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12H8m8 0l-4 4m4-4l-4-4m12 4A10 10 0 1112 2a10 10 0 0110 10z"
                  />
                </svg>
                Contact via Email
              </motion.a>
            </div>
          </form>
        </motion.div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-gray-100">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-6 grid md:grid-cols-3 gap-10 text-center"
        >
          <div>
            <h3 className="text-lg font-semibold text-indigo-600">Email</h3>
            <a
              href="mailto:thecodexaoffical@gmail.com"
              className="text-gray-700 hover:text-indigo-600 transition"
            >
              thecodexaoffical@gmail.com
            </a>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-indigo-600">Phone</h3>
            <p className="text-gray-700">Phone: +923254472055</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-indigo-600">Location</h3>
            <p className="text-gray-700">Remote | Global Team</p>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
